import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Award, BadgeCheck, Pencil, Plus, Trash2, X } from "lucide-react";
import DataTable from "../../../components/DataTable";
import ConfirmationModal from "../../../components/ConfirmationModal";
import CreatableCombobox from "../../../components/CreatableCombobox";
import { searchBrands, createBrand, updateBrand, deleteBrand, searchCategories } from "../../../services/materialAdmin";
import { codeFromName } from "./CategoryModal";

const TIERS = [
  { value: "BUDGET", label: "Budget" },
  { value: "STANDARD", label: "Standard" },
  { value: "PREMIUM", label: "Premium" },
  { value: "LUXURY", label: "Luxury" },
];
const TIER_BADGE = {
  BUDGET: "bg-slate-100 text-slate-600 border-slate-200",
  STANDARD: "bg-sky-50 text-sky-700 border-sky-200",
  PREMIUM: "bg-violet-50 text-violet-700 border-violet-200",
  LUXURY: "bg-amber-50 text-amber-700 border-amber-200",
};

const EMPTY = {
  brandCode: "", brandName: "", tier: "STANDARD", country: "India", website: "",
  logoUrl: "", description: "", isVerified: false, isActive: true, categoryIds: [],
};

const Field = ({ label, value, onChange, required, placeholder }) => (
  <div>
    <label className="text-xs font-medium text-slate-600">
      {label} {required && <span className="text-flamingo-600">*</span>}
    </label>
    <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500" />
  </div>
);

const BrandsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [codeTouched, setCodeTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await searchBrands({ isActive: null, withCategories: true });
      setRows(res?.items || []);
    } catch (e) { toast.error("Failed to load brands"); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const cats = await searchCategories({ isActive: null });
        setCategories((cats?.items || []).map((c) => ({ value: c.materialCategoryId, label: c.categoryName })));
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setCodeTouched(false); setModalOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      brandCode: b.brandCode, brandName: b.brandName, tier: b.tier || "STANDARD",
      country: b.country || "", website: b.website || "", logoUrl: b.logoUrl || "",
      description: b.description || "", isVerified: !!b.isVerified, isActive: b.isActive ?? true,
      categoryIds: (b.categories || []).map((c) => c.materialCategoryId),
    });
    setCodeTouched(true);
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.brandName?.trim() || (!editing && !form.brandCode?.trim())) {
      return toast.error("Brand code and name are required");
    }
    setSaving(true);
    try {
      const payload = {
        brandName: form.brandName.trim(), tier: form.tier, country: form.country || "India",
        website: form.website || null, logoUrl: form.logoUrl || null, description: form.description || null,
        isVerified: form.isVerified, isActive: form.isActive, categoryIds: form.categoryIds,
      };
      if (editing) {
        await updateBrand(editing.materialBrandId, payload);
        toast.success("Brand updated");
      } else {
        await createBrand({ ...payload, brandCode: form.brandCode.trim().toUpperCase() });
        toast.success("Brand created");
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save brand");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    const { id } = confirm;
    setConfirm(null);
    try {
      await deleteBrand(id);
      toast.success("Brand deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete brand");
    }
  };

  const columns = [
    { key: "brand", header: "Brand", width: 240, render: (r) => (
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
          {r.logoUrl ? <img src={r.logoUrl} alt="" className="max-h-full max-w-full object-contain" /> : <Award size={16} className="text-slate-300" />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
            {r.brandName}
            {r.isVerified && <BadgeCheck size={14} className="text-sky-500 shrink-0" />}
          </p>
          <p className="text-xs text-slate-500">{r.brandCode}</p>
        </div>
      </div>
    )},
    { key: "tier", header: "Tier", width: 110, render: (r) => (
      <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${TIER_BADGE[r.tier] || TIER_BADGE.STANDARD}`}>{r.tier || "STANDARD"}</span>
    )},
    { key: "country", header: "Country", width: 110, render: (r) => <span className="text-sm text-slate-600">{r.country || "—"}</span> },
    { key: "cats", header: "Categories", width: 260, render: (r) => (
      <div className="flex flex-wrap gap-1">
        {(r.categories || []).slice(0, 3).map((c) => (
          <span key={c.materialCategoryId} className="px-2 py-0.5 rounded-md bg-flamingo-50 text-flamingo-700 border border-flamingo-100 text-[11px]">{c.categoryName}</span>
        ))}
        {(r.categories || []).length > 3 && <span className="text-[11px] text-slate-400">+{r.categories.length - 3}</span>}
        {(!r.categories || r.categories.length === 0) && <span className="text-xs text-slate-400">—</span>}
      </div>
    )},
    { key: "status", header: "Status", width: 150, render: (r) => (
      <div className="flex flex-wrap items-center gap-1">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${r.isActive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${r.isActive ? "bg-emerald-500" : "bg-rose-500"}`} />
          {r.isActive ? "ACTIVE" : "INACTIVE"}
        </span>
        {r.isPendingReview && (
          <span className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-semibold">PENDING REVIEW</span>
        )}
      </div>
    )},
    { key: "actions", header: "Actions", width: 110, align: "center", render: (r) => (
      <div className="flex items-center justify-center gap-1.5">
        <button title="Edit" onClick={() => openEdit(r)} className="w-8 h-8 rounded-md text-slate-500 hover:text-flamingo-600 hover:bg-flamingo-50 inline-flex items-center justify-center">
          <Pencil size={15} />
        </button>
        <button title="Delete" onClick={() => setConfirm({ id: r.materialBrandId, name: r.brandName })} className="w-8 h-8 rounded-md text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center">
          <Trash2 size={15} />
        </button>
      </div>
    )},
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Manufacturers in the catalog and the categories they operate in.</p>
        <button onClick={openAdd} className="inline-flex items-center gap-2 rounded-xl bg-flamingo-600 hover:bg-flamingo-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors">
          <Plus size={16} /> Add Brand
        </button>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        minWidth={960}
        rowKey={(r, i) => r?.materialBrandId ?? i}
        emptyState={
          <div className="px-6 py-12 text-center">
            <Award size={28} className="mx-auto text-slate-300" />
            <p className="text-base font-semibold text-slate-900 mt-2">No brands yet</p>
            <p className="text-sm text-slate-500 mt-1">Add a brand and link it to the categories it makes materials for.</p>
          </div>
        }
      />

      {/* Add/Edit modal */}
      <div className={`fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 ${modalOpen ? "" : "hidden"}`}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="text-base font-semibold text-slate-900">{editing ? "Edit Brand" : "Add Brand"}</h3>
            <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Brand Name" value={form.brandName} required placeholder="Kajaria"
              onChange={(v) => setForm((p) => ({
                ...p, brandName: v,
                brandCode: !editing && !codeTouched ? codeFromName(v) : p.brandCode,
              }))} />
            <Field label="Brand Code" value={form.brandCode} required={!editing} placeholder="KAJARIA"
              onChange={(v) => { setCodeTouched(true); setForm({ ...form, brandCode: v.toUpperCase() }); }} />
            <div>
              <label className="text-xs font-medium text-slate-600">Tier</label>
              <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}
                className="mt-1 w-full rounded-xl border border-slate-300 bg-white py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500">
                {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <Field label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
            <Field label="Website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} placeholder="https://…" />
            <Field label="Logo URL" value={form.logoUrl} onChange={(v) => setForm({ ...form, logoUrl: v })} placeholder="https://…" />
            <div className="sm:col-span-2">
              <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
            </div>
            <div className="sm:col-span-2">
              <CreatableCombobox
                label="Categories this brand operates in"
                isMulti
                value={form.categoryIds}
                onChange={(vals) => setForm({ ...form, categoryIds: vals })}
                options={categories}
                placeholder="Select categories…"
              />
            </div>
            <div className="sm:col-span-2 flex items-center gap-6">
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                <input type="checkbox" checked={form.isVerified} onChange={(e) => setForm({ ...form, isVerified: e.target.checked })}
                  className="rounded border-slate-300 text-flamingo-600 focus:ring-flamingo-500/40" />
                Verified brand
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  className="rounded border-slate-300 text-flamingo-600 focus:ring-flamingo-500/40" />
                Active
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
            <button onClick={() => setModalOpen(false)} className="rounded-xl border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2.5 hover:bg-white">Cancel</button>
            <button onClick={save} disabled={saving} className="rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5">
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!confirm}
        onConfirm={doDelete}
        onCancel={() => setConfirm(null)}
        message={`Delete brand "${confirm?.name}"? Blocked if any materials still use it.`}
      />
    </div>
  );
};

export default BrandsTab;
